import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DeleteOutlineRounded,
  DownloadRounded,
  FolderOpenOutlined,
  UploadFileRounded,
  VisibilityOutlined,
} from "@mui/icons-material";
import color from "../../../Constants/colors";
import { DealDocument } from "../../../Models/ambassador";
import {
  DealSide,
  dealDocumentUrl,
  downloadDealDocument,
} from "../../../Api/ambassador";
import { downloadDocumentBlob } from "../../../Helpers/documentHelper";
import { getFileTypeStyle } from "../../../Helpers/fileTypePresentation";
import ImagePreviewModal from "../../Common/ImagePreviewModal";
import PdfPreviewModal from "../../Common/PdfPreviewModal";
import AmbassadorConfirmDialog from "./AmbassadorConfirmDialog";
import { EmptyState } from "./AmbassadorUi";
import { formatDateTime } from "./format";

// Mirrors the backend (ambassador_deal_document_service.py).
const ACCEPT =
  ".pdf,.png,.jpg,.jpeg,.gif,.webp,.txt,.csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx";
const MAX_FILE_SIZE = 20 * 1024 * 1024;
const MAX_FILES = 10;

const formatSize = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

interface Props<T extends DealDocument> {
  side: DealSide;
  dealId: number;
  documents: T[];
  /** Without it the library is read-only. */
  onUpload?: (files: File[]) => Promise<unknown>;
  onDelete: (documentId: number) => Promise<unknown>;
  canDelete: (document: T) => boolean;
  pending: boolean;
}

/** Every document of a deal, from the ambassador and from como. Images and
 * PDFs open in the platform's viewers, everything else is downloaded. */
const DealDocuments = <T extends DealDocument>({
  side,
  dealId,
  documents,
  onUpload,
  onDelete,
  canDelete,
  pending,
}: Props<T>) => {
  const { t, i18n } = useTranslation("ambassador");
  const input = useRef<HTMLInputElement | null>(null);
  const [preview, setPreview] = useState<T | null>(null);
  const [deleting, setDeleting] = useState<T | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [tooLarge, setTooLarge] = useState<string[]>([]);

  const pick = async (fileList: FileList | null) => {
    const files = Array.from(fileList ?? []);
    if (input.current) input.current.value = "";
    if (!onUpload || !files.length) return;
    const large = files.filter((file) => file.size > MAX_FILE_SIZE);
    setTooLarge(large.map((file) => file.name));
    const accepted = files.filter((file) => file.size <= MAX_FILE_SIZE);
    if (!accepted.length) return;
    try {
      await onUpload(accepted.slice(0, MAX_FILES));
    } catch {
      // The request already showed what went wrong.
    }
  };

  const download = async (document: T) => {
    setDownloadingId(document.id);
    try {
      const response = await downloadDealDocument(side, dealId, document.id);
      downloadDocumentBlob(response, document.filename);
    } catch {
      // The request already showed what went wrong.
    } finally {
      setDownloadingId(null);
    }
  };

  const open = (document: T) =>
    document.previewable ? setPreview(document) : download(document);

  const isPdf = preview?.content_type === "application/pdf";
  const previewUrl = preview
    ? dealDocumentUrl(side, dealId, preview.id)
    : undefined;

  return (
    <Box>
      {onUpload && (
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1.5,
            mb: 2,
          }}
        >
          <Typography sx={{ fontSize: 13, color: color.gray500 }}>
            {t("documents.uploadHint")}
          </Typography>
          <input
            ref={input}
            type="file"
            multiple
            hidden
            accept={ACCEPT}
            onChange={(event) => pick(event.target.files)}
          />
          <Button
            variant="contained"
            startIcon={
              pending ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <UploadFileRounded />
              )
            }
            disabled={pending}
            onClick={() => input.current?.click()}
            sx={{ flexShrink: 0 }}
          >
            {t("documents.upload")}
          </Button>
        </Box>
      )}
      {tooLarge.length > 0 && (
        <Typography sx={{ fontSize: 13, color: color.red600, mb: 2 }}>
          {t("documents.tooLarge", { files: tooLarge.join(", ") })}
        </Typography>
      )}

      {documents.length === 0 ? (
        <EmptyState
          icon={<FolderOpenOutlined />}
          title={t("documents.emptyTitle")}
          text={t("documents.emptyText")}
        />
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              md: "repeat(2, minmax(0, 1fr))",
            },
            gap: 1.5,
          }}
        >
          {documents.map((document) => {
            const { Icon, iconColor, iconBg } = getFileTypeStyle(
              document.filename,
            );
            return (
              <Box
                key={document.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: "12px",
                  border: `1px solid ${color.gray200}`,
                  backgroundColor: color.white,
                }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => open(document)}
                  aria-label={
                    document.previewable
                      ? t("documents.open", { name: document.filename })
                      : t("documents.download", { name: document.filename })
                  }
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flex: 1,
                    minWidth: 0,
                    p: 0,
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    "&:hover .document-name": { textDecoration: "underline" },
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      flexShrink: 0,
                      borderRadius: "10px",
                      backgroundColor: iconBg,
                      color: iconColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ fontSize: 22 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      className="document-name"
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: color.gray900,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {document.filename}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: color.gray500 }}>
                      {t(
                        document.from_ambassador
                          ? "documents.byAmbassador"
                          : "documents.byComo",
                        { name: document.uploaded_by_name ?? "–" },
                      )}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: color.gray400 }}>
                      {formatDateTime(document.created_at, i18n.language)} ·{" "}
                      {formatSize(document.size)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", flexShrink: 0 }}>
                  {document.previewable && (
                    <Tooltip title={t("documents.preview")}>
                      <IconButton size="small" onClick={() => open(document)}>
                        <VisibilityOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={t("documents.downloadAction")}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={downloadingId === document.id}
                        onClick={() => download(document)}
                      >
                        {downloadingId === document.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DownloadRounded fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  {canDelete(document) && (
                    <Tooltip title={t("common.delete")}>
                      <IconButton
                        size="small"
                        onClick={() => setDeleting(document)}
                      >
                        <DeleteOutlineRounded fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <PdfPreviewModal
        open={Boolean(preview) && isPdf}
        onClose={() => setPreview(null)}
        url={previewUrl}
        documentName={preview?.filename}
      />
      <ImagePreviewModal
        open={Boolean(preview) && !isPdf}
        onClose={() => setPreview(null)}
        url={previewUrl}
        documentName={preview?.filename}
      />
      <AmbassadorConfirmDialog
        open={deleting !== null}
        title={t("documents.deleteTitle")}
        text={t("documents.deleteText", { name: deleting?.filename ?? "" })}
        confirmLabel={t("common.delete")}
        danger
        pending={pending}
        onClose={() => setDeleting(null)}
        onConfirm={() =>
          deleting &&
          onDelete(deleting.id)
            .then(() => setDeleting(null))
            .catch(() => undefined)
        }
      />
    </Box>
  );
};

export default DealDocuments;
